import { gameCategories, type GameConfig } from './games';

/**
 * 智能推荐模式的数据层：问卷 → 检查结果 → 异常分析 → 训练计划。
 *
 * 设计要点：
 * - 问卷/检查项都归一到「异常项（Finding）」这一个中间层，分析只依赖它，
 *   这样后续增删题目或检查项目不需要改推荐逻辑。
 * - 每个异常项声明它对应的训练分类与偏好的玩法类型，推荐时据此选题，
 *   避免出现「眼干却推空间知觉」这类不匹配。
 */

export type FindingGroup = 'accommodation' | 'convergence' | 'fusion' | 'refractive' | 'surface' | 'cognitive';

export interface FindingMeta {
  id: string;
  label: string;
  /** 面向家长的通俗解释 */
  detail: string;
  group: FindingGroup;
  /** 推荐分类（按优先级排列） */
  categories: string[];
  /** 在分类内优先选择的玩法类型；取值必须是 games.ts 里真实出现过的 type，写错只会退化成默认排序 */
  preferTypes?: string[];
}

export const FINDING_GROUP_LABEL: Record<FindingGroup, string> = {
  accommodation: '调节功能',
  convergence: '集合功能',
  fusion: '融合与双眼协同',
  refractive: '屈光状态',
  surface: '眼表与视疲劳',
  cognitive: '视觉认知与注意',
};

export const FINDINGS: FindingMeta[] = [
  {
    id: 'accommodation_lag',
    label: '调节反应滞后',
    detail: '看近处时对焦偏慢，长时间读写容易模糊、串行。',
    group: 'accommodation',
    categories: ['myopia', 'fatigue'],
    preferTypes: ['flip', 'focus'],
  },
  {
    id: 'accommodation_inflexible',
    label: '调节灵活度不足',
    detail: '远近交替切换时对焦速度跟不上，抄板书、看远看近转换吃力。',
    group: 'accommodation',
    categories: ['myopia', 'binocular'],
    preferTypes: ['flip', 'focus'],
  },
  {
    id: 'convergence_insufficiency',
    label: '集合不足',
    detail: '看近处双眼向内聚拢的力量偏弱，容易眼胀、重影、注意力涣散。',
    group: 'convergence',
    categories: ['binocular', 'fatigue'],
    preferTypes: ['fusion', 'focus'],
  },
  {
    id: 'convergence_excess',
    label: '集合过度',
    detail: '看近处双眼过度向内聚拢，长时间近距离用眼后眼部紧绷。',
    group: 'convergence',
    categories: ['binocular', 'fatigue'],
    preferTypes: ['fusion'],
  },
  {
    id: 'fusion_instability',
    label: '融合功能不稳定',
    detail: '维持双眼同时看清的能力不足，干扰下容易出现复视或跳视。',
    group: 'fusion',
    categories: ['binocular'],
    preferTypes: ['fusion', 'tracking'],
  },
  {
    id: 'suppression',
    label: '单眼抑制倾向',
    detail: '一只眼的信号被大脑忽略，双眼信息没有真正合并。',
    group: 'fusion',
    categories: ['amblyopia', 'binocular'],
    preferTypes: ['fusion', 'color'],
  },
  {
    id: 'stereo_weak',
    label: '立体视偏弱',
    detail: '判断远近深浅的能力不足，接球、上下台阶容易失误。',
    group: 'fusion',
    categories: ['binocular', 'spatial'],
    preferTypes: ['spatial'],
  },
  {
    id: 'saccade_weak',
    label: '扫视与眼球控制偏弱',
    detail: '快速定位目标的眼动不精准，阅读容易漏字、跳行。',
    group: 'cognitive',
    categories: ['spatial', 'memory'],
    preferTypes: ['tracking', 'coordination'],
  },
  {
    id: 'far_blur',
    label: '远视力下降',
    detail: '看远处模糊、习惯眯眼，提示屈光或调节状态需要关注。',
    group: 'refractive',
    categories: ['myopia'],
    preferTypes: ['focus', 'flip'],
  },
  {
    id: 'near_blur',
    label: '近视力下降',
    detail: '看近处吃力、需要拉远距离，读写耐受度下降。',
    group: 'accommodation',
    categories: ['myopia', 'fatigue'],
    preferTypes: ['focus'],
  },
  {
    id: 'myopia_risk',
    label: '近视进展风险',
    detail: '屈光或眼轴指标提示近视发生/加深风险偏高。',
    group: 'refractive',
    categories: ['myopia', 'fatigue'],
    preferTypes: ['focus', 'flip', 'exercise'],
  },
  {
    id: 'astigmatism',
    label: '散光',
    detail: '光学成像在两个方向上不一致，容易看久疲劳、重影。',
    group: 'refractive',
    categories: ['amblyopia', 'myopia'],
    preferTypes: ['focus', 'color', 'spatial'],
  },
  {
    id: 'contrast_weak',
    label: '对比敏感度偏低',
    detail: '在低对比或昏暗环境下辨认细节吃力。',
    group: 'surface',
    categories: ['amblyopia'],
    preferTypes: ['color', 'spatial'],
  },
  {
    id: 'color_weak',
    label: '色觉辨别偏弱',
    detail: '对相近颜色的区分不够敏锐。',
    group: 'surface',
    categories: ['amblyopia'],
    preferTypes: ['color', 'spatial'],
  },
  {
    id: 'asthenopia',
    label: '视疲劳症状',
    detail: '眼酸、眼胀或眼干，用眼后不适感明显。',
    group: 'surface',
    categories: ['fatigue'],
    preferTypes: ['exercise'],
  },
  {
    id: 'photophobia',
    label: '畏光',
    detail: '对强光敏感，光线变化时不适。',
    group: 'surface',
    categories: ['fatigue'],
    preferTypes: ['exercise'],
  },
  {
    id: 'low_endurance',
    label: '近距离用眼耐受度低',
    detail: '近距离用眼维持时间偏短，很快出现不适。',
    group: 'surface',
    categories: ['fatigue', 'myopia'],
    preferTypes: ['exercise', 'focus'],
  },
  {
    id: 'attention_weak',
    label: '视觉注意与记忆偏弱',
    detail: '阅读时注意力难以维持，视觉信息记忆效率偏低。',
    group: 'cognitive',
    categories: ['memory'],
    preferTypes: ['memory'],
  },
];

export const FINDING_MAP: Record<string, FindingMeta> = Object.fromEntries(FINDINGS.map(f => [f.id, f]));

/* ------------------------------------------------------------------ */
/* 问卷                                                                */
/* ------------------------------------------------------------------ */

export interface AssessmentOption {
  label: string;
  /** 命中即记为异常项；不填表示该选项正常 */
  finding?: string;
  /** 1 = 轻度，2 = 明显 */
  severity?: 1 | 2;
}

export interface AssessmentQuestion {
  id: string;
  section: 'function' | 'symptom';
  text: string;
  hint?: string;
  options: AssessmentOption[];
}

export const SECTION_LABEL: Record<AssessmentQuestion['section'], string> = {
  function: '双眼视功能状态',
  symptom: '异常症状',
};

export const ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'q_convergence',
    section: 'function',
    text: '孩子看近处（读书、写字）时，是否容易出现串行、跳行或重影？',
    hint: '提示双眼向内聚拢的持续能力是否足够',
    options: [
      { label: '从不', },
      { label: '偶尔出现', finding: 'convergence_insufficiency', severity: 1 },
      { label: '经常出现', finding: 'convergence_insufficiency', severity: 2 },
    ],
  },
  {
    id: 'q_switch',
    section: 'function',
    text: '从看远切换到看近（或反过来）时，是否明显需要更长时间才能看清？',
    hint: '提示调节灵活度与对焦速度',
    options: [
      { label: '切换很快，没有感觉' },
      { label: '略慢，需要几秒', finding: 'accommodation_inflexible', severity: 1 },
      { label: '明显慢，常看不清', finding: 'accommodation_inflexible', severity: 2 },
    ],
  },
  {
    id: 'q_suppression',
    section: 'function',
    text: '孩子是否出现过歪头、闭上一只眼或凑很近看东西的情况？',
    hint: '提示可能存在单眼抑制',
    options: [
      { label: '从未' },
      { label: '偶尔', finding: 'suppression', severity: 1 },
      { label: '经常', finding: 'suppression', severity: 2 },
    ],
  },
  {
    id: 'q_depth',
    section: 'function',
    text: '判断距离时是否容易失误（如接球、上下台阶、拿杯子）？',
    hint: '提示立体视的精细程度',
    options: [
      { label: '不失误，判断准确' },
      { label: '偶尔判断不准', finding: 'stereo_weak', severity: 1 },
      { label: '经常判断不准', finding: 'stereo_weak', severity: 2 },
    ],
  },
  {
    id: 'q_reading',
    section: 'function',
    text: '阅读或抄写时，是否容易漏字、错行，或必须用手指指着读？',
    hint: '提示扫视与眼球运动控制',
    options: [
      { label: '不会' },
      { label: '偶尔会', finding: 'saccade_weak', severity: 1 },
      { label: '经常发生', finding: 'saccade_weak', severity: 2 },
    ],
  },
  {
    id: 'q_endurance',
    section: 'function',
    text: '孩子连续近距离用眼多久后会开始明显不适？',
    hint: '衡量近距离用眼耐受度',
    options: [
      { label: '1 小时以上' },
      { label: '20-40 分钟', finding: 'low_endurance', severity: 1 },
      { label: '不到 20 分钟', finding: 'low_endurance', severity: 2 },
    ],
  },
  {
    id: 'q_asthenopia',
    section: 'symptom',
    text: '用眼后是否出现眼酸、眼胀或眼干？',
    options: [
      { label: '从不' },
      { label: '偶尔（长时间用眼后）', finding: 'asthenopia', severity: 1 },
      { label: '经常出现', finding: 'asthenopia', severity: 2 },
    ],
  },
  {
    id: 'q_headache',
    section: 'symptom',
    text: '用眼后是否出现头痛或太阳穴发胀？',
    options: [
      { label: '从不' },
      { label: '偶尔', finding: 'asthenopia', severity: 1 },
      { label: '经常且影响学习', finding: 'asthenopia', severity: 2 },
    ],
  },
  {
    id: 'q_far',
    section: 'symptom',
    text: '看远处（黑板、路牌）是否模糊，或需要眯眼才能看清？',
    options: [
      { label: '清晰，不眯眼' },
      { label: '偶尔模糊', finding: 'far_blur', severity: 1 },
      { label: '经常模糊或眯眼', finding: 'far_blur', severity: 2 },
    ],
  },
  {
    id: 'q_near',
    section: 'symptom',
    text: '看近处（课本、屏幕）是否吃力，或需要把东西拿远一点？',
    options: [
      { label: '轻松看清' },
      { label: '偶尔吃力', finding: 'near_blur', severity: 1 },
      { label: '经常吃力', finding: 'near_blur', severity: 2 },
    ],
  },
  {
    id: 'q_light',
    section: 'symptom',
    text: '在强光下或光线变化时，是否明显比同龄人更敏感、容易眯眼？',
    options: [
      { label: '不敏感' },
      { label: '略敏感', finding: 'photophobia', severity: 1 },
      { label: '明显畏光', finding: 'photophobia', severity: 2 },
    ],
  },
  {
    id: 'q_attention',
    section: 'symptom',
    text: '阅读或看屏幕时，是否容易走神、注意力难以维持？',
    options: [
      { label: '能持续专注' },
      { label: '偶尔走神', finding: 'attention_weak', severity: 1 },
      { label: '经常走神', finding: 'attention_weak', severity: 2 },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* 检查项目（手动录入）                                                  */
/* ------------------------------------------------------------------ */

export type CheckLevel = 'normal' | 'mild' | 'marked' | 'unknown';

export const CHECK_LEVEL_LABEL: Record<CheckLevel, string> = {
  normal: '正常',
  mild: '轻度异常',
  marked: '明显异常',
  unknown: '未检查',
};

export interface CheckItem {
  id: string;
  name: string;
  /** 正常参考范围，面向家长可读 */
  normal: string;
  unit?: string;
  /** 异常时产出的异常项 */
  finding: string;
}

export interface CheckItemGroup {
  id: string;
  name: string;
  items: CheckItem[];
}

export const CHECK_ITEM_GROUPS: CheckItemGroup[] = [
  {
    id: 'acuity',
    name: '视力与屈光',
    items: [
      { id: 'va_far', name: '裸眼远视力', normal: '≥ 1.0', finding: 'far_blur' },
      { id: 'va_near', name: '裸眼近视力', normal: '≥ 1.0', finding: 'near_blur' },
      { id: 'refraction', name: '屈光度（等效球镜）', normal: '0 ～ -0.50 D', unit: 'D', finding: 'myopia_risk' },
      { id: 'astigmatism', name: '散光度数', normal: '< 0.75', unit: 'D', finding: 'astigmatism' },
      { id: 'axial', name: '眼轴长度', normal: '随年龄，参考 < 24', unit: 'mm', finding: 'myopia_risk' },
    ],
  },
  {
    id: 'accommodation',
    name: '调节功能',
    items: [
      { id: 'amp', name: '调节幅度', normal: '≥ 年龄对应值（约 15-0.25×年龄）', unit: 'D', finding: 'accommodation_lag' },
      { id: 'facility', name: '调节灵活度（翻转拍）', normal: '单眼 ≥ 12，双眼 ≥ 8', unit: 'cpm', finding: 'accommodation_inflexible' },
      { id: 'mem', name: '调节反应（MEM）', normal: '+0.25 ～ +0.75', unit: 'D', finding: 'accommodation_lag' },
    ],
  },
  {
    id: 'convergence',
    name: '集合与融合',
    items: [
      { id: 'npc', name: '集合近点（NPC）', normal: '≤ 6', unit: 'cm', finding: 'convergence_insufficiency' },
      { id: 'phoria_far', name: '远距隐斜', normal: '±2', unit: '△', finding: 'fusion_instability' },
      { id: 'phoria_near', name: '近距隐斜', normal: '0 ～ -6（外隐斜）', unit: '△', finding: 'convergence_insufficiency' },
      { id: 'ac_a', name: 'AC/A 比值', normal: '4 ～ 6', unit: '△/D', finding: 'convergence_insufficiency' },
      { id: 'fusion_range', name: '融合范围', normal: '辐辏 ≥ +25，散开 ≥ -4', unit: '△', finding: 'fusion_instability' },
    ],
  },
  {
    id: 'visual',
    name: '视觉质量',
    items: [
      { id: 'stereo', name: '立体视锐度', normal: '≤ 60', unit: '秒', finding: 'stereo_weak' },
      { id: 'worth4', name: 'Worth 四点灯', normal: '看到 4 点', finding: 'suppression' },
      { id: 'contrast', name: '对比敏感度', normal: '各空间频率在正常曲线内', finding: 'contrast_weak' },
      { id: 'color', name: '色觉检查', normal: '正常', finding: 'color_weak' },
    ],
  },
];

export const ALL_CHECK_ITEMS: CheckItem[] = CHECK_ITEM_GROUPS.flatMap(g => g.items);

export function checkItemById(id: string): CheckItem | undefined {
  return ALL_CHECK_ITEMS.find(i => i.id === id);
}

/* ------------------------------------------------------------------ */
/* 分析                                                                */
/* ------------------------------------------------------------------ */

export interface AssessmentAnswer {
  /** questionId → 选项下标 */
  [questionId: string]: number;
}

export interface AssessmentCheckState {
  /** checkItemId → 结果等级 */
  [itemId: string]: CheckLevel;
}

export interface DetectedFinding {
  id: string;
  meta: FindingMeta;
  /** 累计严重度，用于排序 */
  severity: number;
  /** 触发来源描述，例如「问卷：阅读跳行」「检查：集合近点」 */
  sources: string[];
}

export interface PlanGame {
  id: string;
  name: string;
  icon: string;
  category: string;
  reason: string;
}

export interface RecommendedPlan {
  id: string;
  name: string;
  focus: string;
  description: string;
  duration: number;
  priority: 'high' | 'medium' | 'low';
  games: PlanGame[];
}

export interface AssessmentAnalysis {
  findings: DetectedFinding[];
  plans: RecommendedPlan[];
  /** 归一化后的 0-100 视觉功能指数，越高越好 */
  healthScore: number;
  summary: string;
}

/** 只把问卷与检查结果转成异常项，便于单测与复用 */
export function detectFindings(answers: AssessmentAnswer, checks: AssessmentCheckState): DetectedFinding[] {
  const acc = new Map<string, DetectedFinding>();

  const push = (findingId: string, severity: number, source: string) => {
    if (!FINDING_MAP[findingId]) return;
    const existing = acc.get(findingId);
    if (existing) {
      existing.severity += severity;
      if (!existing.sources.includes(source)) existing.sources.push(source);
    } else {
      acc.set(findingId, { id: findingId, meta: FINDING_MAP[findingId], severity, sources: [source] });
    }
  };

  ASSESSMENT_QUESTIONS.forEach(question => {
    const choice = answers[question.id];
    if (typeof choice !== 'number') return;
    const option = question.options[choice];
    if (!option?.finding) return;
    push(option.finding, option.severity ?? 1, `问卷 · ${question.text.slice(0, 12)}…`);
  });

  Object.entries(checks).forEach(([itemId, level]) => {
    if (level !== 'mild' && level !== 'marked') return;
    const item = checkItemById(itemId);
    if (!item) return;
    push(item.finding, level === 'marked' ? 2 : 1, `检查 · ${item.name}`);
  });

  return [...acc.values()].sort((a, b) => b.severity - a.severity);
}

function pickGames(categoryId: string, preferTypes: string[] | undefined, limit: number): GameConfig[] {
  const category = gameCategories.find(c => c.id === categoryId);
  if (!category) return [];
  const pool = category.games.filter(g => g.difficulty <= 2);
  const ordered = [
    ...pool.filter(g => preferTypes ? preferTypes.includes(g.type) : false),
    ...pool.filter(g => !(preferTypes ? preferTypes.includes(g.type) : false)),
  ];
  // 低门槛优先，再按 id 稳定排序，保证同一份评估每次给出同一套计划
  const seen = new Set<string>();
  const picked: GameConfig[] = [];
  for (const game of ordered) {
    if (seen.has(game.id)) continue;
    seen.add(game.id);
    picked.push(game);
    if (picked.length >= limit) break;
  }
  return picked.sort((a, b) => a.unlockEnergy - b.unlockEnergy || a.id.localeCompare(b.id));
}

export function analyzeAssessment(answers: AssessmentAnswer, checks: AssessmentCheckState): AssessmentAnalysis {
  const findings = detectFindings(answers, checks);

  // 分类归因：异常项累加到主分类，次分类只作少量加成。
  // 权重刻意让次分类远低于主分类，避免「眼酸 + 头痛」这类同源症状叠加后
  // 把真正的功能性问题（如集合不足）挤出主方案。
  const categoryScore = new Map<string, { score: number; reasons: string[] }>();
  findings.forEach(finding => {
    const [primary, ...rest] = finding.meta.categories;
    [primary, ...rest].forEach((categoryId, index) => {
      const entry = categoryScore.get(categoryId) ?? { score: 0, reasons: [] };
      entry.score += index === 0 ? finding.severity + 1 : finding.severity * 0.4;
      if (index === 0) entry.reasons.push(finding.meta.label);
      categoryScore.set(categoryId, entry);
    });
  });

  const ranked = [...categoryScore.entries()].sort((a, b) => b[1].score - a[1].score);
  const categoryName = (id: string) => gameCategories.find(c => c.id === id)?.name ?? id;
  // 分类可能只由某个异常项的次分类贡献，所以先找主分类命中的，再退到任意命中
  const focusOf = (categoryId: string) => findings.find(f => f.meta.categories[0] === categoryId)
    ?? findings.find(f => f.meta.categories.includes(categoryId));

  const plans: RecommendedPlan[] = [];
  const top = ranked.slice(0, 2);

  if (top.length > 0) {
    const [primaryId, primaryMeta] = top[0];
    const focusFinding = focusOf(primaryId);
    const games = pickGames(primaryId, focusFinding?.meta.preferTypes, 3).map(game => ({
      id: game.id,
      name: game.name,
      icon: game.icon,
      category: categoryName(primaryId),
      reason: focusFinding ? `针对${focusFinding.meta.label}` : '补强当前薄弱环节',
    }));
    if (games.length > 0) {
      plans.push({
        id: `plan-${primaryId}`,
        name: `${categoryName(primaryId)}强化方案`,
        focus: focusFinding?.meta.label ?? '当前主要问题',
        description: `优先改善${primaryMeta.reasons.slice(0, 2).join('、') || '当前视觉功能瓶颈'}，训练强度由低到高递进。`,
        duration: games.length * 4,
        priority: 'high',
        games,
      });
    }
  }

  if (top.length > 1) {
    const [secondaryId, secondaryMeta] = top[1];
    const focusFinding = focusOf(secondaryId);
    const games = pickGames(secondaryId, focusFinding?.meta.preferTypes, 2).map(game => ({
      id: game.id,
      name: game.name,
      icon: game.icon,
      category: categoryName(secondaryId),
      reason: focusFinding ? `辅助改善${focusFinding.meta.label}` : '均衡补充训练',
    }));
    if (games.length > 0) {
      plans.push({
        id: `plan-${secondaryId}`,
        name: `${categoryName(secondaryId)}辅助方案`,
        focus: focusFinding?.meta.label ?? '均衡补充',
        description: `配合主方案，兼顾${secondaryMeta.reasons.slice(0, 2).join('、') || '整体视觉功能'}。`,
        duration: games.length * 4,
        priority: 'medium',
        games,
      });
    }
  }

  // 疲劳症状单独给一个放松方案，避免只做「练」不做「松」
  const hasFatigue = findings.some(f => f.meta.group === 'surface');
  if (hasFatigue && !plans.some(p => p.id === 'plan-fatigue')) {
    const games = pickGames('fatigue', ['exercise'], 2).map(game => ({
      id: game.id,
      name: game.name,
      icon: game.icon,
      category: categoryName('fatigue'),
      reason: '缓解眼疲劳，训练后放松',
    }));
    if (games.length > 0) {
      plans.push({
        id: 'plan-fatigue',
        name: '视疲劳放松方案',
        focus: '眼肌放松与调节恢复',
        description: '每次训练后做一遍，缓解眼酸眼胀，提升用眼耐受度。',
        duration: games.length * 3,
        priority: 'medium',
        games,
      });
    }
  }

  if (plans.length === 0) {
    const games = pickGames('myopia', ['focus', 'flip'], 2).map(game => ({
      id: game.id,
      name: game.name,
      icon: game.icon,
      category: categoryName('myopia'),
      reason: '维持调节功能，预防近视',
    }));
    plans.push({
      id: 'plan-maintain',
      name: '日常维护方案',
      focus: '维持当前视觉状态',
      description: '未发现明显异常，建议保持规律训练，稳定调节与双眼协调能力。',
      duration: games.length * 4,
      priority: 'low',
      games,
    });
  }

  const totalSeverity = findings.reduce((sum, f) => sum + f.severity, 0);
  const healthScore = Math.max(40, Math.min(100, 100 - totalSeverity * 6));
  const summary = findings.length === 0
    ? '问卷与检查结果未发现明显异常，建议保持规律训练。'
    : `共识别 ${findings.length} 项异常，主要集中在${[...new Set(findings.slice(0, 3).map(f => FINDING_GROUP_LABEL[f.meta.group]))].join('、')}。`;

  return { findings, plans, healthScore, summary };
}

/* ------------------------------------------------------------------ */
/* 持久化结构                                                          */
/* ------------------------------------------------------------------ */

export interface VisionAssessment {
  id: string;
  answers: AssessmentAnswer;
  checks: AssessmentCheckState;
  /** 检查报告缩略图（data URL），仅用于回看，不做识别 */
  reportImage?: string;
  findingIds: string[];
  createdAt: string;
}

export interface TrainingPlanSnapshot {
  id: string;
  name: string;
  focus: string;
  duration: number;
  priority: RecommendedPlan['priority'];
  games: PlanGame[];
  generatedAt: string;
}
